import type { CommandSpec } from '../../lib/shortcuts/index.js';
import { useLayout } from '../../state/layout.js';
import { useProcgen } from '../../state/procgen.js';
import { defaultOutput, instanceName } from './procgen-doc.js';

/** The dock tool panel id (generators.md section 2). */
const PANEL = 'procgen';

/** Show the panel, or hide it when it is already on screen (like the Profiler). */
function togglePanel(): void {
  const layout = useLayout.getState();
  const el = typeof document === 'undefined' ? null : (document.querySelector(`[data-dock-panel="${PANEL}"]`) as HTMLElement | null);
  if (el?.isConnected && el.offsetParent !== null) layout.toggle(PANEL);
  else layout.show(PANEL);
}

/** Window / Procedural commands (registered by the shell next to PROFILER_COMMANDS). */
export const PROCGEN_COMMANDS: readonly CommandSpec[] = [
  {
    id: 'view.procgen',
    label: 'Procedural',
    category: 'Window',
    icon: 'layers',
    keywords: 'procgen procedural generator worldgen biomes scatter noise rules',
    run: togglePanel,
  },
  {
    id: 'procgen.new',
    label: 'Procedural: New Generator',
    category: 'Procedural',
    icon: 'plus',
    keywords: 'procgen create generator procgen.json',
    run: async () => {
      useLayout.getState().show(PANEL);
      await useProcgen.getState().createNew('gen', 'New Generator');
    },
  },
  {
    id: 'procgen.preview',
    label: 'Procedural: Preview Selected Output',
    category: 'Procedural',
    icon: 'eye',
    keywords: 'procgen render image show output',
    enabled: () => useProcgen.getState().doc !== null,
    run: () => {
      const s = useProcgen.getState();
      if (!s.doc || !s.path) return;
      useLayout.getState().show(PANEL);
      // The focused output (from the inspector / a diagnostic), else the default one.
      const focused = s.focus?.match(/^outputs\.([A-Za-z_][A-Za-z0-9_]*)/)?.[1];
      const output = focused && s.doc.outputs[focused] ? focused : defaultOutput(s.doc);
      if (!output) return;
      const ref = `${instanceName(s.doc, s.path)}.${output}`;
      if (s.settings.show === ref) void s.runPreview();
      else s.setSettings({ show: ref, scope: null });
    },
  },
  {
    id: 'procgen.check',
    label: 'Procedural: Check Generator',
    category: 'Procedural',
    icon: 'check',
    keywords: 'procgen validate diagnostics errors',
    enabled: () => useProcgen.getState().doc !== null,
    run: () => useProcgen.getState().check(),
  },
];
